import { useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import rootStore from '~/store';
import { DEFAULT_VIEW_MODE, isValidViewMode, PROJECT_VIEW_PARAM, type ProjectViewMode } from '../types';

const VIEW_MODE_STORAGE_KEY = 'homeViewV2.viewMode';

function getStoredViewMode(): ProjectViewMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored && isValidViewMode(stored)) return stored;
    return null;
  } catch {
    return null;
  }
}

function setStoredViewMode(mode: ProjectViewMode): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Read and write view mode (table | map | split).
 * Priority: URL param if present and valid → last saved viewMode in localStorage → default.
 * Persists the user's choice to localStorage when they change viewMode (scoped to homeViewV2).
 */
export function useViewMode(): {
  viewMode: ProjectViewMode;
  setViewMode: (mode: ProjectViewMode) => void;
} {
  const location = useLocation();
  const navigate = useNavigate();

  const viewMode = useMemo(() => {
    const params = new URLSearchParams(location.searchStr);
    const urlValue = params.get(PROJECT_VIEW_PARAM);
    if (urlValue && isValidViewMode(urlValue)) return urlValue;
    const stored = getStoredViewMode();
    if (stored) return stored;
    return DEFAULT_VIEW_MODE;
  }, [location.searchStr]);

  const setViewMode = useCallback(
    (mode: ProjectViewMode) => {
      const previous = viewMode;
      setStoredViewMode(mode);
      const params = new URLSearchParams(location.searchStr);
      params.set(PROJECT_VIEW_PARAM, mode);
      const next = params.toString();
      navigate({ to: `${location.pathname}${next ? `?${next}` : ''}`, replace: true });

      // Skip synthetic toggle on mount (page calls setViewMode internally to enforce default).
      if (previous !== mode) {
        captureTypedPosthogEvent(POSTHOG_EVENTS.projectMapViewToggled, {
          organization_id: rootStore.currentOrganizationId || undefined,
          source: 'home',
          from: previous,
          to: mode,
        });
      }
    },
    [navigate, location, viewMode],
  );

  return { viewMode, setViewMode };
}
